import { useCallback, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { Promotion } from '../../../../dto/promo.types';
import { useCoinsAnimation } from '../../../../hooks/useCoinsAnimation';
import { useStores } from '../../../../stores/useStores';
import { formatValueWithK } from '../../../../utils/helpers';
import Button from '../../../buttons/Button';
import { DialogBox } from '../../../Dialog/DialogBox';
import { DialogContent } from '../../../Dialog/DialogContent';
import { DialogHeader } from '../../../Dialog/DialogHeader';
import { ErrorMessage } from '../../../ErrorMessage/ErrorMessage';
import { SocialCoin } from '../../../SocialCoin/SocialCoin';
import { SweepCoin } from '../../../SweepCoin/SweepCoin';
import { getImageByCoinsType } from '../utils';

import styles from './ClaimPromoGiftDialog.module.scss';

const ClaimPromoGiftDialog = observer(() => {
  const { walletStore, portalStore, promotionStore } = useStores();
  const { t } = useTranslation();

  const [errorMsg, setErrorMsg] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { startAnimation } = useCoinsAnimation();

  const { options } = portalStore;

  const promotion = options.promotion as Promotion;
  const promotionExists = !!promotion;

  const removePcQueryParam = useCallback(() => {
    if (searchParams.has('pc')) {
      searchParams.delete('pc');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const closeDialog = useCallback(() => {
    removePcQueryParam();
    promotionStore.closePromotionDialog();
  }, [promotionStore, removePcQueryParam]);

  const onCloseClick = () => {
    promotionStore.sendAnalyticsEvent({ event_action: 'click', click_location: 'close' });
    closeDialog();
  };

  useEffect(() => {
    if (!promotionExists) {
      closeDialog();
    }
  }, [promotionExists, closeDialog]);

  const handleSubmit = async () => {
    if (errorMsg) {
      setErrorMsg('');
    }

    setIsClaiming(true);
    promotionStore.sendAnalyticsEvent({
      event_action: 'click',
      click_location: 'cta',
      button_text: t('common.buttons.claim'),
      action_result: 'claim_gift',
    });
    const updatedWalletData = await promotionStore.redeemPromoCode(promotion.promoCode);
    if (updatedWalletData) {
      const { gcCoin, scCoin, totalScCoin } = updatedWalletData;
      walletStore.updateWallet({ gcCoin, scCoin, totalScCoin });
      startAnimation({
        gcAmount: promotion.gcAmount,
        scAmount: promotion.scAmount,
        onEnd: () => closeDialog(),
      });
    } else {
      setErrorMsg('Failed to claim gift');
      setIsClaiming(false);
    }
  };

  const title = typeof promotion.title === 'string' ? promotion.title : 'Claim Gift';

  return (
    <DialogBox title={promotion.title}>
      <DialogHeader title={title} onClose={onCloseClick} />

      <DialogContent className={styles.content}>
        <div className={styles.subtitle}>{t('common.enjoyYourGift')}</div>
        <img
          alt="Gift Image"
          className={styles.giftImg}
          src={getImageByCoinsType(promotion.gcAmount, promotion.scAmount)}
        />
        <div className={styles.amounts}>
          {promotion.gcAmount > 0 && (
            <div className={styles.amount}>
              <SocialCoin alt="Social Coin" />
              <div>{formatValueWithK(promotion.gcAmount, t)}</div>
            </div>
          )}
          {promotion.scAmount > 0 && <div className={styles.plus}>+</div>}
          {promotion.scAmount > 0 && (
            <div className={styles.amount}>
              <SweepCoin alt="Sweep Coin" />
              <div>{promotion.scAmount.toLocaleString()}</div>
            </div>
          )}
        </div>
        <Button btnType="main" disabled={isClaiming} onClick={handleSubmit}>
          {t('common.buttons.claim')}
        </Button>
        <ErrorMessage className={styles.error}>{errorMsg}</ErrorMessage>
      </DialogContent>
    </DialogBox>
  );
});

export default ClaimPromoGiftDialog;
